/**
 * Runtime zod mirrors of the API response entities, used only to validate the
 * *shape* of list responses at the SDK boundary (unknown network JSON in, a value
 * we trust in). These intentionally use permissive inner types (`z.unknown()`,
 * loose enums via `z.string()`) for nested JSONB blobs whose only real schema
 * lives server-side (`condition`, `configuration`, `params`, `details`) — we
 * validate envelope + id + timestamp shape, not reimplement server validation.
 */
import { z } from "zod";
import {
  assetFilterSchema,
  assetTypeSchema,
  channelTypeSchema,
  checkTypeSchema,
  maintenanceWindowPolicySchema,
  pageSchema,
  reportFormatSchema,
  reportTypeSchema,
  severitySchema,
  sloObjectiveSchema,
  statusPageVisibilitySchema,
  timestampSchema,
  uuidSchema,
} from "./common";

const nullableTimestamp = timestampSchema.nullable();

export const monitorScopeSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  scope_type: z.string(),
  value: z.string(),
  normalised_value: z.string(),
  verification_method: z.string(),
  verification_status: z.string(),
  approved_by: uuidSchema.nullable(),
  approved_at: nullableTimestamp,
  expires_at: nullableTimestamp,
  rate_limit_policy_id: uuidSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
  revoked_at: nullableTimestamp,
});

export const assetSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  asset_type: assetTypeSchema,
  display_name: z.string(),
  normalised_identifier: z.string(),
  criticality: z.string(),
  environment: z.string(),
  owner_user_id: uuidSchema.nullable(),
  scope_id: uuidSchema.nullable(),
  agent_id: uuidSchema.nullable(),
  first_seen_at: nullableTimestamp,
  last_seen_at: nullableTimestamp,
  last_healthy_at: nullableTimestamp,
  status: z.string(),
  tags: z.array(z.string()),
  created_at: timestampSchema,
  updated_at: timestampSchema,
  deleted_at: nullableTimestamp,
});

export const checkSummarySchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  asset_id: uuidSchema,
  check_type: checkTypeSchema,
  name: z.string(),
  enabled: z.boolean(),
  interval_seconds: z.number(),
  timeout_seconds: z.number(),
  failure_threshold: z.number(),
  recovery_threshold: z.number(),
  consecutive_failures: z.number(),
  consecutive_successes: z.number(),
  last_run_at: nullableTimestamp,
  next_run_at: nullableTimestamp,
  status: z.string(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const checkDefinitionSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  asset_id: uuidSchema,
  check_type: checkTypeSchema,
  name: z.string(),
  enabled: z.boolean(),
  interval_seconds: z.number(),
  timeout_seconds: z.number(),
  failure_threshold: z.number(),
  recovery_threshold: z.number(),
  configuration: z.record(z.unknown()),
  last_run_at: nullableTimestamp,
  next_run_at: nullableTimestamp,
  status: z.string(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const checkObservationSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  check_id: uuidSchema,
  asset_id: uuidSchema,
  status: z.string(),
  latency_ms: z.number().nullable(),
  details: z.unknown(),
  error: z.string().nullable(),
  observed_at: timestampSchema,
});

export const storedEventSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  asset_id: uuidSchema.nullable(),
  source_type: z.string(),
  event_type: z.string(),
  severity: severitySchema,
  summary: z.string(),
  payload_hash: z.string().nullable(),
  redaction_status: z.string(),
  dedupe_hash: z.string().nullable(),
  repeat_count: z.number(),
  observed_at: timestampSchema,
  ingested_at: timestampSchema,
  expires_at: nullableTimestamp,
});

export const alertRuleSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  name: z.string(),
  enabled: z.boolean(),
  condition: z.record(z.unknown()),
  severity: severitySchema,
  dedupe_key_template: z.string(),
  for_duration_seconds: z.number(),
  recovery_duration_seconds: z.number(),
  route_id: uuidSchema.nullable(),
  maintenance_window_policy: maintenanceWindowPolicySchema,
  asset_filter: assetFilterSchema.nullish(),
  created_by: uuidSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const alertRouteSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  name: z.string(),
  enabled: z.boolean(),
  channel_type: channelTypeSchema,
  severity_filter: z.array(severitySchema),
  asset_filter: assetFilterSchema.nullish(),
  schedule_id: uuidSchema.nullable(),
  escalation_policy_id: uuidSchema.nullable(),
  include_payload: z.boolean(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const alertSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  rule_id: uuidSchema,
  asset_id: uuidSchema.nullable(),
  state: z.string(),
  severity: severitySchema,
  dedupe_key: z.string(),
  summary: z.string(),
  suppressed_by_window: uuidSchema.nullable(),
  acknowledged_by: uuidSchema.nullable(),
  acknowledged_at: nullableTimestamp,
  incident_id: uuidSchema.nullable(),
  opened_at: timestampSchema,
  last_seen_at: timestampSchema,
  resolved_at: nullableTimestamp,
});

export const incidentSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  title: z.string(),
  severity: severitySchema,
  status: z.string(),
  asset_ids: z.array(uuidSchema),
  owner_user_id: uuidSchema.nullable(),
  opened_at: timestampSchema,
  acknowledged_at: nullableTimestamp,
  resolved_at: nullableTimestamp,
  closed_at: nullableTimestamp,
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const incidentDetailSchema = incidentSchema.extend({
  summary: z.string().nullable(),
  root_cause: z.string().nullable(),
});

export const timelineEventSchema = z.object({
  id: uuidSchema,
  incident_id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  event_type: z.string(),
  actor_type: z.string(),
  actor_id: uuidSchema.nullable(),
  summary: z.string(),
  created_at: timestampSchema,
});

export const maintenanceWindowSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  name: z.string(),
  timezone: z.string(),
  start_time: timestampSchema,
  end_time: timestampSchema,
  days_of_week: z.array(z.number()).nullable(),
  asset_filter: assetFilterSchema.nullish(),
  suppress_alerts: z.boolean(),
  show_status_page: z.boolean(),
  created_by: uuidSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const sloSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  asset_id: uuidSchema,
  name: z.string(),
  objective_type: sloObjectiveSchema,
  target_percentage: z.number(),
  window_days: z.number(),
  good_event_query: z.string(),
  total_event_query: z.string(),
  burn_rate_alerts: z.array(z.object({ factor: z.number(), window_hours: z.number() })),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const sloResultSchema = z.object({
  good: z.number(),
  total: z.number(),
  attained_pct: z.number().nullable(),
  target_pct: z.number(),
  error_budget_total: z.number(),
  error_budget_consumed: z.number(),
  error_budget_remaining_pct: z.number().nullable(),
  burn_rates: z.array(z.tuple([z.number(), z.number().nullable()])),
});

export const sloStatusResponseSchema = z.object({
  slo: sloSchema,
  result: sloResultSchema,
  limitations: z.string(),
});

export const statusPageSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  visibility: statusPageVisibilitySchema,
  slug: z.string(),
  title: z.string(),
  service_groups: z.array(z.object({ name: z.string(), asset_ids: z.array(uuidSchema) })),
  incident_visibility_policy: z.string(),
  maintenance_visibility_policy: z.string(),
  expose_identifiers: z.boolean(),
  published: z.boolean(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const reportSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  report_type: reportTypeSchema,
  format: reportFormatSchema,
  params: z.unknown(),
  status: z.string(),
  redaction_applied: z.boolean(),
  artifact_path: z.string().nullable(),
  error: z.string().nullable(),
  requested_by: uuidSchema.nullable(),
  created_at: timestampSchema,
  completed_at: nullableTimestamp,
});

export const auditListItemSchema = z.object({
  id: uuidSchema,
  seq: z.number(),
  tenant_id: uuidSchema.nullable(),
  workspace_id: uuidSchema.nullable(),
  actor_type: z.string(),
  actor_id: uuidSchema.nullable(),
  action: z.string(),
  target_type: z.string(),
  target_id: z.string().nullable(),
  ts: timestampSchema,
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  integrity_hash: z.string(),
  previous_hash: z.string(),
});

export const auditExportItemSchema = auditListItemSchema
  .omit({ tenant_id: true, workspace_id: true })
  .extend({ metadata: z.unknown() });

export const auditVerifyResultSchema = z.object({
  verified_records: z.number(),
  intact: z.boolean(),
  first_tampered_seq: z.number().nullable(),
});

export const agentSummarySchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  workspace_id: uuidSchema,
  name: z.string(),
  status: z.string(),
  cert_serial: z.string().nullable(),
  cert_not_after: nullableTimestamp,
  agent_version: z.string().nullable(),
  os: z.string().nullable(),
  kernel: z.string().nullable(),
  enrolled_at: nullableTimestamp,
  last_heartbeat_at: nullableTimestamp,
  revoked_at: nullableTimestamp,
  created_at: timestampSchema,
});

export const tenantSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  retention_days: z.number(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const workspaceSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});

export const meResponseSchema = z.object({
  actor_type: z.string(),
  actor_id: uuidSchema.nullable(),
  tenant_id: uuidSchema.nullable(),
  workspace_id: uuidSchema.nullable(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  is_platform: z.boolean(),
});

export const loginResponseSchema = z.object({
  token: z.string(),
  user_id: uuidSchema,
  tenant_id: uuidSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Page<T> parsers for every list endpoint the app calls.
// ---------------------------------------------------------------------------
export const scopesPageSchema = pageSchema(monitorScopeSchema);
export const assetsPageSchema = pageSchema(assetSchema);
export const checksPageSchema = pageSchema(checkSummarySchema);
export const checkObservationsPageSchema = pageSchema(checkObservationSchema);
export const eventsPageSchema = pageSchema(storedEventSchema);
export const alertsPageSchema = pageSchema(alertSchema);
export const alertRulesPageSchema = pageSchema(alertRuleSchema);
export const alertRoutesPageSchema = pageSchema(alertRouteSchema);
export const incidentsPageSchema = pageSchema(incidentSchema);
export const timelinePageSchema = pageSchema(timelineEventSchema);
export const maintenanceWindowsPageSchema = pageSchema(maintenanceWindowSchema);
export const slosPageSchema = pageSchema(sloSchema);
export const statusPagesPageSchema = pageSchema(statusPageSchema);
export const reportsPageSchema = pageSchema(reportSchema);
export const agentsPageSchema = pageSchema(agentSummarySchema);
export const auditListPageSchema = pageSchema(auditListItemSchema);
export const auditExportListSchema = z.object({ items: z.array(auditExportItemSchema) });
export const tenantsListSchema = z.object({ items: z.array(tenantSchema) });
export const workspacesListSchema = z.object({ items: z.array(workspaceSchema) });
